import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { liveLines, newId, type CountPreview } from '@gk/contracts'
import { useClub, useEngineState } from '../../club/ClubProvider.tsx'
import { unitsFor, unitSymbol, useLatestVersions, useProducts, useStockLocations, useStockMovements, type ProductRow, type StockMovementRow } from '../../data/catalog.ts'
import {
  buildCountClosePayload, buildCountLinePayload, closedRows, closeProblem, COUNT_PROBLEM_KEY, countedOf, countingRows, lineProblem, lineVerdict, previewCount, reconcileRows, uncounted,
  useStockCountLines, useStockCounts, type CountingRow, type ReconcileRow, type StockCountLineRow, type StockCountRow,
} from '../../data/countUi.ts'
import { locationLabel } from '../../data/stockUi.ts'
import { fmtNum } from '../../lib/format.ts'
import { formatLocal } from '../../lib/time.ts'
import type { Pending } from '../../sync/merge.ts'
import { BackLink, Banner, DisabledHint, errorText, Field, NotAllowed, Pill } from '../../ui/components.tsx'
import { useAction, useWorkshopAction, useWriteAccess } from '../../ui/hooks.ts'
import { TwoTap } from '../workshop/shared.tsx'
import { usePersonNames } from '../measures/shared.tsx'
import { Chevron, Feedback, useLang, useStockAccess } from './shared.tsx'

const signed = (n: number, lang: 'fr' | 'en'): string => `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmtNum(Math.abs(n), lang, 3)}`

/** M15-B, Stock › Inventaire (docs/M15.md B and "Screens"; `/catalogue/count/$countId`, Z's route): one count of one place.
 *  - Open, for whoever records stock movements (stock.record; on the shared workstation under the person's PIN, rule 8): the products as 56-px rows, each
 *    folding open a numeric field (the keypad), its unit and an optional reason; "Compter" sends `stock.count_line@1`, a FACT kept on the phone when the
 *    storeroom has no signal. ONE WAY OF COUNTING: no expected quantity anywhere on a product until its line is typed; then the line shows what was counted,
 *    the expected (the line's own snapshot; the device's estimate while pending) and the difference. Typing a product again makes a later line (the latest
 *    typed is the one that counts, the server keeps both).
 *  - Open, for the storeroom (stock.manage): the reconciliation — every live line against the place's on-hand NOW, the difference, the counter's reason or a
 *    field for one where a difference has none, the pills of a book that moved since the line and of two counts that disagree, the products left as they
 *    are — and "Clôturer l'inventaire" in two taps (an online intent; the server's verdict shows here, its flags in the success line).
 *  - Closed: what the close did (counted, expected at close, the adjustment, the reason), from the server's rows. */
export function CountPage() {
  const { t } = useTranslation()
  const lang = useLang()
  const { countId } = useParams({ strict: false }) as { countId?: string }
  const { site } = useClub()
  const record = useWriteAccess('stock.record')
  const manage = useStockAccess()
  const counts = useStockCounts(); const lines = useStockCountLines(); const locations = useStockLocations(); const products = useProducts(); const movements = useStockMovements()
  const names = usePersonNames()
  // the close's verdict outlives the reconciliation it came from (the page turns to the result once the count is closed): its line and its flags stay
  const closing = useWorkshopAction()
  const count = (counts ?? []).find((c) => c.id === countId)
  const mine = useMemo(() => (lines ?? []).filter((l) => l.count_id === countId), [lines, countId])
  if (!record.permitted && !manage.permitted) return <section className="flex flex-col gap-4" data-testid="count-page"><h1 className="text-2xl font-bold">{t('count.title')}</h1><NotAllowed permission="stock.record" /></section>
  if (!counts || !lines || !locations || !products || !movements) return <p>{t('common.loading')}</p>
  const back = <BackLink to="/catalogue/count">← {t('count.title')}</BackLink>
  if (!count) return <section className="flex flex-col gap-4" data-testid="count-page">{back}<Banner tone="info" testId="count-not-found">{t('count.page.notFound')}</Banner></section>
  const place = locations.find((l) => l.id === count.location_id)
  const placeName = place ? locationLabel(place, lang) : '…'
  const open = count.status === 'open'
  const who = (id: string | null | undefined) => names(id)
  return (
    <section className="flex flex-col gap-4" data-testid="count-page" data-count={count.id} data-status={count.status}>
      {back}
      <h1 className="text-2xl font-bold" data-testid="count-title">{t('count.page.title', { place: placeName })}</h1>
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="count-status"><Pill tone={open ? 'amber' : 'green'}>{open ? t('count.page.statusOpen') : t('count.page.statusClosed')}</Pill></span>
        <span className="meta">{who(count.actor_person_id) ? t('count.page.opened', { date: formatLocal(count.opened_at, site.timezone), name: who(count.actor_person_id) }) : t('count.page.openedNoName', { date: formatLocal(count.opened_at, site.timezone) })}</span>
        {count.closed_at && <span className="meta" data-testid="count-closed-by">{who(count.closed_by_person_id) ? t('count.page.closed', { date: formatLocal(count.closed_at, site.timezone), name: who(count.closed_by_person_id) }) : t('count.page.closedNoName', { date: formatLocal(count.closed_at, site.timezone) })}</span>}
      </div>
      {count.note && <p className="meta" data-testid="count-note">{t('count.page.note', { note: count.note })}</p>}
      {/* release b review (finding 1): whoever both counts and closes (the direction, the mechanic) reaches the reconciliation in one tap from the top; the
          counter's own view is unchanged */}
      {open && record.permitted && manage.permitted && <ReconcileJump lines={liveLines(mine, count.id).size} />}
      {open && record.permitted && <Counting count={count} lines={mine} products={products} movements={movements} />}
      {open && manage.permitted && <Reconcile count={count} lines={mine} products={products} movements={movements} w={closing} />}
      {closing.ok && <Banner tone="ok" testId="count-close-ok">{closing.ok}</Banner>}
      {!open && <Result count={count} lines={mine} products={products} />}
    </section>
  )
}

/** A 56-px row at the top of an open count that takes whoever may close it to the reconciliation, below the counting list. */
function ReconcileJump({ lines }: { lines: number }) {
  const { t } = useTranslation()
  return (
    <button type="button" className="btn w-full justify-between text-left" onClick={() => document.getElementById('count-reconcile')?.scrollIntoView({ block: 'start' })} data-testid="count-reconcile-jump">
      <span>{t('count.reconcile.jump', { count: lines })}</span><span aria-hidden="true">↓</span>
    </button>
  )
}

// ---- counting ----------------------------------------------------------------------------------------------------------------------------------
function Counting({ count, lines, products, movements }: { count: StockCountRow; lines: Array<Pending<StockCountLineRow>>; products: ProductRow[]; movements: readonly StockMovementRow[] }) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [hide, setHide] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const all = useMemo(() => countingRows(products, lines, movements, count), [products, lines, movements, count])
  const rows = useMemo(() => countingRows(products, lines, movements, count, query, hide), [products, lines, movements, count, query, hide])
  const progress = countedOf(all)
  return (
    <section className="flex flex-col gap-3" data-testid="count-counting">
      <h2 className="text-xl font-bold">{t('count.counting.title')}</h2>
      <p className="meta">{t('count.counting.help')}</p>
      <p className="meta" data-testid="count-place-help">{t('count.counting.helpPlace')}</p>
      <p className="font-semibold" data-testid="count-progress" data-counted={progress.counted} data-total={progress.total}>{t('count.counting.progress', { counted: progress.counted, total: progress.total })}</p>
      <Field label={t('count.counting.search')} htmlFor="cc-search">
        <input id="cc-search" type="search" className="input" autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="count-search" />
      </Field>
      <button type="button" className="btn" aria-pressed={hide} onClick={() => setHide(!hide)} data-testid="count-hide-counted">{hide ? t('count.counting.showAll') : t('count.counting.hideCounted')}</button>
      {rows.length === 0 && <p className="meta" data-testid="count-no-match">{t('count.counting.noMatch')}</p>}
      <ul className="flex flex-col gap-3" data-testid="count-products">
        {rows.map((r) => <CountingCard key={r.product.id} row={r} countId={count.id} open={open === r.product.id} onToggle={() => setOpen(open === r.product.id ? null : r.product.id)} onDone={() => setOpen(null)} />)}
      </ul>
    </section>
  )
}

function CountingCard({ row, countId, open, onToggle, onDone }: { row: CountingRow; countId: string; open: boolean; onToggle: () => void; onDone: () => void }) {
  const { t } = useTranslation()
  const lang = useLang()
  const { product, live } = row
  const base = unitSymbol(product.base_unit_code, lang)
  const verdict = live ? lineVerdict(live) : null
  return (
    <li className="card flex flex-col gap-2" data-testid="count-product" data-product={product.id} data-counted={live ? '1' : '0'}>
      <button type="button" className="flex min-h-14 w-full items-center justify-between gap-3 py-1 text-left" aria-expanded={open} aria-controls={`count-${product.id}`} onClick={onToggle} data-testid="count-product-toggle">
        <span className="flex min-w-0 flex-col gap-1">
          <span className="min-w-0 break-words text-lg font-bold" data-testid="count-product-name">{product.name}</span>
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="meta">{base}</span>
            {product.archived_at && <Pill tone="gray">{t('catalogue.archived')}</Pill>}
            {live?._pending && <Pill tone="amber">{t('common.pendingSend')}</Pill>}
          </span>
        </span>
        <Chevron open={open} />
      </button>
      {/* the line, once typed: what was counted, then the expected and the difference (never before: one way of counting) */}
      {live && (
        <div className="flex flex-col gap-1 border-t-2 border-slate-200 pt-2 dark:border-slate-700" data-testid="count-line">
          <p className="font-semibold tabular-nums" data-testid="count-line-counted">{t('count.counting.counted', { qty: fmtNum(Number(live.counted_base), lang, 3), unit: base })}</p>
          {verdict ? (
            <>
              <p className="tabular-nums" data-testid="count-line-expected">{t('count.counting.expected', { qty: fmtNum(verdict.expected, lang, 3), unit: base })}</p>
              <p className={`font-semibold tabular-nums ${verdict.differs ? 'text-amber-900 dark:text-amber-200' : ''}`} data-testid="count-line-diff" data-differs={verdict.differs ? '1' : '0'}>
                {verdict.differs ? t('count.counting.diff', { qty: signed(verdict.diff, lang), unit: base }) : t('count.counting.noDiff')}
              </p>
            </>
          ) : <p className="meta" data-testid="count-line-expected-later">{t('count.counting.expectedLater')}</p>}
          {live.reason && <p className="meta" data-testid="count-line-reason">{t('count.reconcile.reasonOfLine', { reason: live.reason })}</p>}
          {row.lines > 1 && <p className="meta" data-testid="count-line-recounted">{t('count.counting.recounted', { count: row.lines })}</p>}
        </div>
      )}
      {open && <LineForm product={product} countId={countId} again={!!live} onDone={onDone} />}
    </li>
  )
}

/** One product's line: the quantity on the keypad (zero is an empty shelf), its unit, an optional reason; a FACT kept on the phone with no signal. */
function LineForm({ product, countId, again, onDone }: { product: ProductRow; countId: string; again: boolean; onDone: () => void }) {
  const { t } = useTranslation()
  const lang = useLang()
  const { send } = useClub()
  const engine = useEngineState()
  const access = useWriteAccess('stock.record')
  const latest = useLatestVersions()
  const { busy, error, run } = useAction()
  const id = useRef(newId())
  const [quantity, setQuantity] = useState('')
  const [unit, setUnit] = useState('')
  const [reason, setReason] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const version = latest?.get(product.id)
  const units = unitsFor(product.base_unit_code, version?.density_kg_per_l != null)
  const chosenUnit = units.some((u) => u.code === unit) ? unit : product.base_unit_code
  const preview: CountPreview | null = quantity.trim() ? previewCount(quantity, chosenUnit, product, version) : null
  const problem = lineProblem(quantity, preview)
  const base = unitSymbol(product.base_unit_code, lang)
  const submit = (e: FormEvent) => {
    e.preventDefault(); setDone(null)
    if (problem || access.readOnlyMode) return
    void run(async () => {
      await send('stock.count_line@1', buildCountLinePayload({ lineId: id.current, countId, productId: product.id, quantity, unit: chosenUnit, reason }))
      setDone(engine.online === false || !navigator.onLine ? t('count.counting.savedOffline') : t('count.counting.saved'))
      id.current = newId(); setQuantity(''); setReason('')
      onDone()
    })
  }
  return (
    <form id={`count-${product.id}`} onSubmit={submit} noValidate className="flex flex-col gap-3 border-t-2 border-slate-200 pt-3 dark:border-slate-700" data-testid="count-line-form">
      <div className="grid grid-cols-[1fr_auto] gap-3">
        <Field label={again ? t('count.counting.quantityAgain') : t('count.counting.quantity')} htmlFor={`cq-${product.id}`}>
          <input id={`cq-${product.id}`} className="input text-2xl" inputMode="decimal" autoComplete="off" value={quantity} onChange={(e) => { setQuantity(e.target.value); setDone(null) }} data-testid="count-quantity" />
        </Field>
        <Field label={t('count.counting.unit')} htmlFor={`cu-${product.id}`}>
          <select id={`cu-${product.id}`} className="input" value={chosenUnit} onChange={(e) => setUnit(e.target.value)} data-testid="count-unit">
            {units.map((u) => <option key={u.code} value={u.code}>{unitSymbol(u.code, lang)}</option>)}
          </select>
        </Field>
      </div>
      {preview && !preview.ok && <p role="alert" className="text-sm font-medium text-red-700 dark:text-red-300" data-testid="count-preview-error">{preview.code === 'QUANTITY_INVALID' ? t(COUNT_PROBLEM_KEY.quantityInvalid) : errorText(t, preview.code)}</p>}
      {preview?.ok && chosenUnit !== product.base_unit_code && <p className="text-sm font-semibold" data-testid="count-preview">{t('count.counting.preview', { qty: fmtNum(Number(preview.countedBase), lang, 3), unit: base })}</p>}
      <Field label={t('count.counting.reason')} hint={t('count.counting.reasonHint')} htmlFor={`cr-${product.id}`}>
        <input id={`cr-${product.id}`} className="input" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="count-reason" />
      </Field>
      {error && <Banner tone="error">{error}</Banner>}
      {done && <Banner tone="ok" testId="count-line-done">{done}</Banner>}
      <DisabledHint show={!!problem && !done && !access.readOnlyMode} testId="count-line-hint">{problem ? t(COUNT_PROBLEM_KEY[problem]) : ''}</DisabledHint>
      <button className="btn btn-primary text-xl" disabled={busy || access.readOnlyMode || !!problem} data-testid="count-line-save">{busy ? t('common.saving') : again ? t('count.counting.again') : t('count.counting.go')}</button>
    </form>
  )
}

// ---- the reconciliation and the close (stock.manage) --------------------------------------------------------------------------------------------
function Reconcile({ count, lines, products, movements, w }: { count: StockCountRow; lines: Array<Pending<StockCountLineRow>>; products: ProductRow[]; movements: readonly StockMovementRow[]; w: ReturnType<typeof useWorkshopAction> }) {
  const { t } = useTranslation()
  const lang = useLang()
  const access = useStockAccess()
  const [reasons, setReasons] = useState<Record<string, string>>({})
  const rows = useMemo(() => reconcileRows(lines, movements, count, products), [lines, movements, count, products])
  const left = useMemo(() => uncounted(countingRows(products, lines, movements, count)), [products, lines, movements, count])
  const problem = closeProblem(rows, reasons, { online: access.online, allowed: access.allowed })
  const close = () => {
    if (problem) return
    void w.run('stock.count_close@1', buildCountClosePayload(count.id, rows, reasons), 'count.reconcile.saved', { aggregateId: count.id })
  }
  return (
    <section id="count-reconcile" className="card flex scroll-mt-20 flex-col gap-3" data-testid="count-reconcile">
      <h2 className="text-xl font-bold">{t('count.reconcile.title')}</h2>
      <p className="meta">{t('count.reconcile.help')}</p>
      {rows.length === 0 && <p className="meta" data-testid="count-reconcile-none">{t('count.reconcile.none')}</p>}
      {left > 0 && <p className="font-semibold" data-testid="count-uncounted" data-count={left}>{t('count.reconcile.uncounted', { count: left })}</p>}
      <ul className="flex flex-col gap-3" data-testid="count-reconcile-list">
        {rows.map((r) => <ReconcileCard key={r.line.id} r={r} lang={lang} reason={reasons[r.line.id] ?? ''} onReason={(v) => setReasons({ ...reasons, [r.line.id]: v })} />)}
      </ul>
      <Feedback id="count-close" error={w.error} pending={w.pending} />
      <DisabledHint show={!!problem && !w.busy} testId="count-close-hint">{problem ? t(COUNT_PROBLEM_KEY[problem]) : ''}</DisabledHint>
      <TwoTap label={t('count.reconcile.go')} confirm={t('count.reconcile.confirm')} onConfirm={close} disabled={w.busy || !!problem} danger className="w-full" testId="count-close" />
    </section>
  )
}

function ReconcileCard({ r, lang, reason, onReason }: { r: ReconcileRow; lang: 'fr' | 'en'; reason: string; onReason: (v: string) => void }) {
  const { t } = useTranslation()
  const base = unitSymbol(r.line.base_unit_code, lang)
  const tone = r.differs ? '!border-amber-400 dark:!border-amber-600' : ''
  return (
    <li className={`flex flex-col gap-2 rounded-xl border-2 border-slate-200 p-3 dark:border-slate-700 ${tone}`} data-testid="count-reconcile-row" data-product={r.line.product_id} data-differs={r.differs ? '1' : '0'}>
      <p className="min-w-0 break-words text-lg font-bold">{r.product?.name ?? '…'}</p>
      <p className="flex flex-wrap gap-x-3 gap-y-1 tabular-nums">
        <span data-testid="count-reconcile-counted">{t('count.counting.counted', { qty: fmtNum(Number(r.line.counted_base), lang, 3), unit: base })}</span>
        <span data-testid="count-reconcile-expected">{t('count.reconcile.expectedNow', { qty: fmtNum(r.expectedNow, lang, 3), unit: base })}</span>
        <span className="font-semibold" data-testid="count-reconcile-diff">{r.differs ? t('count.counting.diff', { qty: signed(r.diff, lang), unit: base }) : t('count.counting.noDiff')}</span>
      </p>
      <span className="flex flex-wrap gap-1.5">
        {r.movedSince && <span data-testid="count-reconcile-moved"><Pill tone="amber">{t('count.reconcile.moved')}</Pill></span>}
        {r.conflict && <span data-testid="count-reconcile-conflict"><Pill tone="amber">{t('count.reconcile.conflict')}</Pill></span>}
        {r.line._pending && <Pill tone="amber">{t('common.pendingSend')}</Pill>}
      </span>
      {r.differs && r.line.reason && <p className="meta" data-testid="count-reconcile-line-reason">{t('count.reconcile.reasonOfLine', { reason: r.line.reason })}</p>}
      {r.needsReason && (
        <Field label={t('count.reconcile.reason')} htmlFor={`rr-${r.line.id}`}>
          <input id={`rr-${r.line.id}`} className="input" maxLength={200} value={reason} onChange={(e) => onReason(e.target.value)} data-testid="count-reconcile-reason" />
        </Field>
      )}
    </li>
  )
}

// ---- a closed count -------------------------------------------------------------------------------------------------------------------------------
function Result({ count, lines, products }: { count: StockCountRow; lines: StockCountLineRow[]; products: ProductRow[] }) {
  const { t } = useTranslation()
  const lang = useLang()
  const movements = useStockMovements()
  const rows = useMemo(() => closedRows(lines, count, products, movements ?? []), [lines, count, products, movements])
  const adjusted = rows.filter((r) => r.adjustment !== null).length
  return (
    <section className="flex flex-col gap-3" data-testid="count-result">
      <h2 className="text-xl font-bold">{t('count.result.title')}</h2>
      <p className="font-semibold" data-testid="count-result-adjusted" data-count={adjusted}>{adjusted > 0 ? t('count.result.adjusted', { count: adjusted }) : t('count.result.noAdjustment')}</p>
      <ul className="flex flex-col gap-3" data-testid="count-result-list">
        {rows.map((r) => {
          const base = unitSymbol(r.line.base_unit_code, lang)
          return (
            <li key={r.line.id} className="card flex flex-col gap-1" data-testid="count-result-row" data-product={r.line.product_id} data-adjusted={r.adjustment !== null ? '1' : '0'}>
              <p className="min-w-0 break-words text-lg font-bold">{r.product?.name ?? '…'}</p>
              <p className="flex flex-wrap gap-x-3 gap-y-1 tabular-nums">
                <span>{t('count.counting.counted', { qty: fmtNum(Number(r.line.counted_base), lang, 3), unit: base })}</span>
                {r.expected !== null && <span>{t('count.result.expectedAtClose', { qty: fmtNum(r.expected, lang, 3), unit: base })}</span>}
                <span className="font-semibold" data-testid="count-result-adjustment">{r.adjustment !== null ? t('count.result.adjustment', { qty: signed(r.adjustment, lang), unit: base }) : t('count.counting.noDiff')}</span>
              </p>
              {(r.movedSince || r.conflict) && (
                <span className="flex flex-wrap gap-1.5">
                  {r.movedSince && <span data-testid="count-result-moved"><Pill tone="amber">{t('count.reconcile.moved')}</Pill></span>}
                  {r.conflict && <span data-testid="count-result-conflict"><Pill tone="amber">{t('count.reconcile.conflict')}</Pill></span>}
                </span>
              )}
              {r.adjustment !== null && r.line.reason && <p className="meta" data-testid="count-result-reason">{t('count.reconcile.reasonOfLine', { reason: r.line.reason })}</p>}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
